import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  BookOpen, 
  MapPin, 
  Scale, 
  ShieldCheck, 
  Activity, 
  AlertTriangle, 
  Flame, 
  Trash2, 
  Compass, 
  FileCheck, 
  Eye, 
  EyeOff,
  Clock,
  ShieldAlert,
  FileText,
  ExternalLink,
  Layers
} from 'lucide-react';
import { PdfViewer } from './PdfViewer';
import { SAFETY_INDUCTION_PDF_URL } from '../constants';

interface SafetyInductionSlidesProps {
  lang: 'id' | 'en';
}

interface SlideContent {
  id: number;
  icon: React.ReactNode;
  category: { id: string; en: string };
  title: { id: string; en: string };
  points: { id: string; en: string }[];
  visualId?: 'cakung_map' | 'sukapura_map' | 'jababeka_map' | 'dasar_hukum' | 'flow_process' | 'bahaya' | 'golden_rules' | 'aturan_umum' | 'sampah' | 'rambu' | 'permit';
}

const SLIDES_DATA: SlideContent[] = [
  {
    id: 1,
    icon: <ShieldCheck className="w-8 h-8 text-yellow-500" />,
    category: { id: "PT United Tractors Tbk", en: "PT United Tractors Tbk" },
    title: { id: "SAFETY INDUCTION - MARKETING DIVISION", en: "SAFETY INDUCTION - MARKETING DIVISION" },
    points: [
      { 
        id: "Selamat datang di Portal Induksi Keselamatan PT United Tractors Tbk (Marketing Division).", 
        en: "Welcome to the Safety Induction Portal of PT United Tractors Tbk (Marketing Division)." 
      },
      { 
        id: "Harap membaca dan memahami panduan keselamatan ini dengan saksama sebagai acuan kerja saat berada di area kerja kami.", 
        en: "Please read and understand this safety guide carefully as a working reference while on our work sites." 
      },
      { 
        id: "Materi ini berisi petunjuk evakuasi darurat, dasar hukum, kebijakan perusahaan, aturan umum yard, manajemen sampah, jenis tanda keselamatan (rambu), dan surat izin kerja risiko tinggi.", 
        en: "This material contains emergency evacuation guidelines, legal base, corporate policies, general yard rules, waste management, safety sign categories, and high-risk work permits." 
      }
    ]
  },
  {
    id: 2,
    icon: <MapPin className="w-8 h-8 text-green-600" />,
    category: { id: "Titik Kumpul / Assembly Point", en: "Assembly Point / Emergency" },
    title: { id: "LOKASI TITIK KUMPUL YARD CAKUNG", en: "CAKUNG YARD ASSEMBLY POINT LOCATION" },
    visualId: "cakung_map",
    points: [
      { 
        id: "Titik Kumpul (Assembly Point) Yard Cakung berada di dekat Loading Area dan Loading Dock.", 
        en: "The Assembly Point for Cakung Yard is located near the Loading Area and Loading Dock." 
      },
      { 
        id: "Apabila terjadi keadaan darurat, dengarkan sirine/instruksi, tetap tenang (tidak panik), dan ikuti jalur evakuasi penunjuk arah.", 
        en: "In case of emergency, listen to the siren/instructions, stay calm (do not panic), and follow the evacuation signs." 
      },
      { 
        id: "Segera menuju titik kumpul terdekat dan ikuti semua arahan dari petugas keselamatan di lokasi.", 
        en: "Immediately head to the nearest assembly point and follow all directions from the safety officer on site." 
      }
    ]
  },
  {
    id: 3,
    icon: <MapPin className="w-8 h-8 text-green-600" />,
    category: { id: "Titik Kumpul / Assembly Point", en: "Assembly Point / Emergency" },
    title: { id: "LOKASI TITIK KUMPUL YARD SUKAPURA", en: "SUKAPURA YARD ASSEMBLY POINT LOCATION" },
    visualId: "sukapura_map",
    points: [
      { 
        id: "Titik Kumpul (Assembly Point) berada di pelataran parkir Yard Marketing United Tractors Sukapura.", 
        en: "The Assembly Point is located in the parking yard of United Tractors Sukapura Marketing Yard." 
      },
      { 
        id: "Tetap tenang dan jangan panik. Segera menuju titik kumpul terdekat dengan berjalan kaki secara tertib.", 
        en: "Stay calm and do not panic. Immediately proceed to the nearest assembly point by walking in an orderly manner." 
      },
      { 
        id: "Ikuti jalur evakuasi hijau dan instruksi dari tim tanggap darurat yang bertugas.", 
        en: "Follow the green evacuation path and instructions from the emergency response team on duty." 
      }
    ]
  },
  {
    id: 4,
    icon: <MapPin className="w-8 h-8 text-green-600" />,
    category: { id: "Titik Kumpul / Assembly Point", en: "Assembly Point / Emergency" },
    title: { id: "LOKASI TITIK KUMPUL YARD JABABEKA", en: "JABABEKA YARD ASSEMBLY POINT LOCATION" },
    visualId: "jababeka_map",
    points: [
      { 
        id: "Titik Kumpul (Assembly Point) berada di Pos Security samping area Pre-Delivery.", 
        en: "The Assembly Point is located at the Security Post beside the Pre-Delivery area." 
      },
      { 
        id: "Perhatikan peta rute penunjuk arah masuk dan keluar serta batas kecepatan berkendara maksimal 10 km/jam.", 
        en: "Heed the layout map of incoming and outgoing routes, and adhere strictly to the maximum speed limit of 10 km/h." 
      },
      { 
        id: "Dilarang memarkir kendaraan darurat menghalangi hidran pemadam kebakaran atau akses evakuasi darurat.", 
        en: "Do not park standard/visiting vehicles obstructing fire hydrants or emergency evacuation shortcuts." 
      }
    ]
  },
  {
    id: 5,
    icon: <Scale className="w-8 h-8 text-blue-600" />,
    category: { id: "Dasar Hukum / Legal", en: "Legal Foundation / Laws" },
    title: { id: "DASAR HUKUM - UU NO. 1 TAHUN 1970", en: "LEGAL FOUNDATION - INDONESIA LAW NO. 1 OF 1970" },
    visualId: "dasar_hukum",
    points: [
      { 
        id: "Pengurus diwajibkan menunjukkan dan menceritakan pada tiap tenaga kerja baru tentang: Kondisi dan bahaya di tempat kerja, alat perlindungan diri (APD), cara-cara & sikap yang aman melakukan pekerjaan.", 
        en: "Management is obligated to demonstrate and explain to every new worker regarding: Conditions and hazards on site, Required PPE, and safe methods and behavior to carry out the work." 
      },
      { 
        id: "Pengurus hanya dapat mempekerjakan tenaga kerja setelah yakin bahwa tenaga kerja telah memahami syarat-syarat keselamatan kerja tersebut.", 
        en: "The management is only authorized to employ workers after confirming that the worker genuinely understands all safety requirements." 
      },
      { 
        id: "Keselamatan Kerja adalah tanggung jawab kita bersama.", 
        en: "Occupational Safety is our shared responsibility." 
      }
    ]
  },
  {
    id: 6,
    icon: <ShieldCheck className="w-8 h-8 text-teal-600" />,
    category: { id: "Kebijakan Manajemen / Corporate Policy", en: "Management Policy & Commitment" },
    title: { id: "KEBIJAKAN MUTU, K3, KEAMANAN & LINGKUNGAN", en: "QUALITY, SAFETY, SECURITY & ENVIRONMENT POLICY" },
    points: [
      { 
        id: "Komitmen manajemen mewujudkan 'Zero Accident' (Nihil Kecelakaan Kerja) dan zero complaint.", 
        en: "Management commitment to achieve 'Zero Accident' (No occupational injuries) and zero complaints." 
      },
      { 
        id: "Menyediakan perlindungan kerja, memenuhi peraturan perundangan K3L, menjaga kelestarian lingkungan dengan program penghematan energi dan pencegahan pencemaran.", 
        en: "Providing work protection, complying with HSE regulations, and conserving the eco-system through energy-saving and pollution prevention." 
      },
      { 
        id: "Meningkatkan kesadaran K3 bagi karyawan, mitra kerja, dan tamu melalui sosialisasi, evaluasi berkelanjutan, dan komitmen aksi nyata.", 
        en: "Enhancing HSE awareness for employees, contractors, and visitors through socializing, ongoing assessments, and proactive action." 
      }
    ]
  },
  {
    id: 7,
    icon: <Compass className="w-8 h-8 text-indigo-600" />,
    category: { id: "Profil Area / Yard Operation", en: "Workspace Operational Profiles" },
    title: { id: "PROFIL AREA OPERASIONAL MARKETING", en: "MARKETING OPERATIONAL YARD PROFILES" },
    points: [
      { 
        id: "1. YARD CAKUNG & USED: Area yard dengan populasi unit Bomag, Light Truck, Bus, dsb. Berada di kawasan PPI Cakung UT HO, Jakarta Timur.", 
        en: "1. CAKUNG & USED YARD: Yard area with Bomag units, Light Trucks, Buses, etc. Located in the PPI Cakung UT HO area, East Jakarta." 
      },
      { 
        id: "2. YARD SUKAPURA & PT. KI: Area yard dengan populasi unit small machine & big machine Komatsu, Tadano, dsb. Berada di kawasan Sukapura Cilincing, Jakarta Utara.", 
        en: "2. SUKAPURA YARD & PT. KI: Yard area with small & large Komatsu machines, Tadano, etc. Located in Sukapura Cilincing, North Jakarta." 
      },
      { 
        id: "3. YARD JABABEKA & PT. UTPE: Area yard dengan populasi stok unitnya truck & small machine Komatsu berada di kawasan Jababeka 1 Cikarang Jawa Barat.", 
        en: "3. JABABEKA YARD & PT. UTPE: Yard area with truck stock and small Komatsu machines in Jababeka 1, Cikarang, West Java." 
      },
      { 
        id: "4. INGGOM: Area pelabuhan di Tanjung Priok untuk pekerjaan assembly unit big machine, bersifat tentative / sementara saja.", 
        en: "4. INGGOM: Port area in Tanjung Priok for large machine assembly, utilized only tentatively / temporarily." 
      }
    ]
  },
  {
    id: 8,
    icon: <Activity className="w-8 h-8 text-orange-600" />,
    category: { id: "Flow Process Yard / Yard Flow", en: "Heavy Equipment Yard Flow" },
    title: { id: "ALUR PROSES DI AREA YARD MARKETING", en: "OPERATIONAL PROCESS FLOW IN MARKETING YARD" },
    visualId: "flow_process",
    points: [
      { 
        id: "Unit In (Alat berat masuk area) -> Pembuatan Surat Rencana Datang (SRD Unit via SAP & System Yard).", 
        en: "Unit In (Heavy equipment entry) -> Creation of Unit Incoming Plan (SRD Unit via SAP & Yard System)." 
      },
      { 
        id: "Unit melalui 4 Alur Proses Utama: [A] Proses Receiving -> [B] Proses Maintenance -> [C] Proses Production -> [D] Proses Pre-Delivery.", 
        en: "The Unit proceeds through 4 main process stages: [A] Receiving -> [B] Maintenance -> [C] Production -> [D] Pre-Delivery." 
      },
      { 
        id: "Proses Pendukung: Proses Supporting Warehouse [E] dan Proses Supporting QA/QC [F] serta diawasi penuh oleh ESR Officer Marketing.", 
        en: "Supporting Processes: Supporting Warehouse [E] and Supporting QA/QC [F], closely monitored by the ESR Marketing Officer." 
      }
    ]
  },
  {
    id: 9,
    icon: <ShieldAlert className="w-8 h-8 text-red-600" />,
    category: { id: "Manajemen Risiko / Hazards", en: "Hazard Identification & Mitigation" },
    title: { id: "MENGENALI SUMBER & FAKTOR BAHAYA", en: "RECOGNIZING HAZARD SOURCES & RISK FACTORS" },
    visualId: "bahaya",
    points: [
      { 
        id: "SUMBER BAHAYA (Sources of Danger): Proses Kerja (Process), Pekerjaan (Occupations), Mesin & Peralatan (Machines & Equipment), Area Kerja (Work Areas), serta Sumber Energi (Energy Sources & Substances).", 
        en: "HAZARD SOURCES: Work Process, Occupations/Job types, Machines & Equipment, Workplace/Work Areas, and Energy Sources & Chemical Substances." 
      },
      { 
        id: "FAKTOR BAHAYA (Risk Factors): Fisik (suara bising, getaran mekanis, suhu panas/dingin, pencahayaan), Kimia (paparan zat, uap, cairan, debu), Biologi (jamur, virus, bakteri di lokasi kerja).", 
        en: "RISK FACTORS: Physical (noise, vibration, temperatures, lighting), Chemical (exposures to vapors, liquids, active dust), Biological (fungi, viruses, airborne bacteria)." 
      },
      { 
        id: "Faktor Ergonomi (posisi kerja tidak alami, gerakan berulang, memegang beban berat) & Faktor Psikososial (stres kerja, beban pikiran berlebih, gesekan/konflik).", 
        en: "Ergonomics (awkward posture, repetitive actions, heavy manual lifting) & Psychosocial Factors (work-related stress, mental fatigue, interpersonal conflicts)." 
      }
    ]
  },
  {
    id: 10,
    icon: <Flame className="w-8 h-8 text-amber-600" />,
    category: { id: "Aturan Emas / Golden Rules", en: "HSE Golden Rules" },
    title: { id: "6 SAFETY GOLDEN RULES (ATURAN EMAS K3)", en: "6 SAFETY GOLDEN RULES (COMPULSORY K3)" },
    visualId: "golden_rules",
    points: [
      { 
        id: "1. KESELAMATAN PERJALANAN: Wajib pakai sabuk pengaman berkendara darat, serta jaket pelampung bila bekerja dekat/di air > 1 meter.", 
        en: "1. TRAVEL SAFETY: Seat belts are mandatory for ground driving, and life jackets are compulsory when working near/on water > 1 meter deep." 
      },
      { 
        id: "2. KENDARAAN & ALAT BERGERAK: Mengemudi wajib memiliki izin khusus (SIM/KIMPER/SIMPER/SIO/TIO), melakukan P2H (pemeriksaan harian), dan dilarang menugaskan karyawan yang sedang sakit/fatigue berat.", 
        en: "2. MACHINERY SAFETY: Operating vehicles requires official permits (SIM/KIMPER/SIO), daily vehicle inspection (P2H), and prohibits assigning sick/fatigued operators." 
      },
      { 
        id: "3. SISTEM ISOLASI, LOCK OUT & TAG OUT (LOTO): Pekerjaan pemeliharaan mesin wajib memasang gembok pengunci (PAD LOCK) dan label khusus LOTO (DANGER TAG & LOCK OUT).", 
        en: "3. ENERGY ISOLATION (LOTO): Maintenance/repair works require inserting personal Pad Locks and safety warning tags (Danger Tag & Lock Out)." 
      },
      { 
        id: "4. BEKERJA DI KETINGGIAN (> 1,8 METER): Wajib mengenakan sabuk keselamatan (Safety Harness) atau perancah (Scaffolding).", 
        en: "4. WORKING AT HEIGHTS (> 1.8 METERS): Full-body Safety Harness or secured scaffolding structures are absolutely required." 
      },
      { 
        id: "5. BEKERJA DI RUANG TERBATAS: Harus mengantongi izin masuk resmi (Confined Space Permit).", 
        en: "5. CONFINED SPACE: Entry into limited access areas requires an authorized Confined Space Permit." 
      },
      { 
        id: "6. ALAT PENGANGKAT & PENYANGGA: Operator harus memastikan beban angkat tidak melebihi Safe Working Load (SWL) maksimum.", 
        en: "6. LIFTING EQUIPMENT: Riggers and operators must ensure loads do not overshoot the certified Safe Working Load (SWL)." 
      }
    ]
  },
  {
    id: 11,
    icon: <ShieldCheck className="w-8 h-8 text-blue-700" />,
    category: { id: "Aturan Umum / General Rules", en: "General Site Regulations" },
    title: { id: "ATURAN UMUM AREA YARD MARKETING", en: "GENERAL SITE RULES IN MARKETING YARD" },
    visualId: "aturan_umum",
    points: [
      { 
        id: "KEWAJIBAN IDENTITAS & APD: Wajib mengikuti program induksi (Initial Safety Briefing), mengenakan ID Card / Pas Tamu di area dada, memakai pakaian seragam rapi, dan menggunakan APD (Safety Helmet, Rompi Reflektif, Safety Shoes).", 
        en: "BADGES & PPE MANDATES: Must participate in the safety induction briefs, display ID Badge on the chest, wear proper uniform, and utilize PPE (Helmet, High-Vis Vest, Safety Shoes)." 
      },
      { 
        id: "KESELAMATAN AREA: Dilarang merokok di luar area khusus (Shelter Merokok). Dilarang mengoperasikan alat listrik tanpa kompetensi. Dilarang mengambil aliran listrik tanpa izin General Affairs.", 
        en: "SITE SAFETY: Smoking outside designated smoking shelters is banned. Operating electric appliances without competency is prohibited. No unsafe connection tapping without General Affairs permits." 
      },
      { 
        id: "AKSES YARD & DISIPLIN: Dilarang masuk area Yard Marketing tanpa izin resmi. Batas kecepatan berkendara maksimal 10 km/jam. Dilarang menggunakan handphone saat mengemudi, dan dilarang memotret tanpa izin.", 
        en: "YARD ACCESS: Trespassing into Marketing Yards without permit is strictly banned. Site driving speed is restricted to 10 km/h. Mobile phone usage or photography/videography without layout authorization is prohibited." 
      }
    ]
  },
  {
    id: 12,
    icon: <Trash2 className="w-8 h-8 text-green-700" />,
    category: { id: "Lingkungan / Environment", en: "Environmental Care & Waste" },
    title: { id: "SISTEM MANAJEMEN SAMPAH & PILAH SAMPAH", en: "WASTE CLASSIFICATION & SORTING GUIDELINES" },
    visualId: "sampah",
    points: [
      { 
        id: "1. ORGANIK (Bak Hijau): Sisa makanan, dedaunan, kertas, tisu keras, dan bahan alami mudah busuk.", 
        en: "1. ORGANIC (Green Bin): Food leftovers, dry leaves, paper scraps, tissues, and bio-degradable items." 
      },
      { 
        id: "2. ANORGANIK (Bak Kuning): Botol plastik, botol plastik minuman, kaleng logam, kaca/beling, wadah bersih.", 
        en: "2. INORGANIC (Yellow Bin): Plastic containers, recyclable bottles, metallic drink cans, glass wares." 
      },
      { 
        id: "3. LIMBAH B3 / BERBAHAYA (Bak Merah): Oli bekas pelumas, aki bekas basah/kering, kain majun terkontaminasi, masker/sarung tangan bekas kotor, baterai bekas, wadah aerosol, dsb.", 
        en: "3. HAZARDOUS WASTE / B3 (Red Bin): Lubricant fluids/used oil, lead batteries, chemical-soaked rags/used cloths, dirty gloves/facemasks, unused batteries, aerosols." 
      },
      { 
        id: "Larangan Keras menyampur atau membuang sampah sembarangan (misal dibuang ke saluran drainase atau dibakar). Kampanyekan pengurangan penggunaan plastik sekali pakai dengan membawa botol minum/wadah sendiri.", 
        en: "Absolutely forbidden to mix hazardous B3 materials with general waste, or litter in gutters/burn trash. Campaign to lower single-use plastic cups/bags by carrying personal water tumblers." 
      }
    ]
  },
  {
    id: 13,
    icon: <AlertTriangle className="w-8 h-8 text-yellow-600" />,
    category: { id: "Rambu / Safety Sign", en: "Safety Signs & Classifications" },
    title: { id: "4 KATEGORI UTAMA RAMBU & TANDA KESELAMATAN", en: "4 MAJOR CATEGORIES OF SAFETY SIGNS & MARKINGS" },
    visualId: "rambu",
    points: [
      { 
        id: "MANDATORY (Wajib): Dilambangkan dengan warna BIRU. Berupa instruksi wajib yang harus dipatuhi. Contoh: Gunakan Alat Pelindung Diri (APD) lengkap.", 
        en: "MANDATORY: Marked with BLUE backgrounds. Expresses compulsory instructions that must be completed. Example: Wear Standard PPE." 
      },
      { 
        id: "WARNING (Awas / Peringatan): Dilambangkan dengan warna KUNING. Menunjukkan potensi bahaya di lokasi tersebut. Contoh: Awas Bahaya Sengatan Listrik, Awas Licin.", 
        en: "WARNING: Marked with YELLOW backgrounds with black accents. Indicates potential hazards around the sector. Example: Danger High Voltage, Slippery Area." 
      },
      { 
        id: "PROHIBITION (Dilarang): Dilambangkan dengan warna MERAH. Larangan keras melakukan suatu kegiatan. Contoh: Dilarang Merokok, Dilarang Mengambil Gambar.", 
        en: "PROHIBITION: Marked with RED circles and slashes. Imposes absolute bans on specific actions. Example: No Smoking, No Unauthorized Cameras." 
      },
      { 
        id: "EMERGENCY (Darurat / Penyelamatan): Dilambangkan dengan warna HIJAU. Petunjuk evakuasi dan fasilitas medis darurat. Contoh: Arah Evakuasi, Kotak P3K, Assembly Point.", 
        en: "EMERGENCY: Marked with GREEN backgrounds. Highlights escape paths, assembly zones, and medical aids. Example: Evacuation Route, First Aid Box." 
      }
    ]
  },
  {
    id: 14,
    icon: <FileCheck className="w-8 h-8 text-indigo-700" />,
    category: { id: "Izin Kerja / Work Permits", en: "Work Authorization Permits" },
    title: { id: "WORK PERMIT UNTUK PEKERJAAN BERISIKO TINGGI", en: "AUTHORIZED SPECIAL PERMITS FOR HIGH-RISK WORK" },
    visualId: "permit",
    points: [
      { 
        id: "Sebelum memulai pekerjaan khusus yang mengandung risiko kecelakaan fatal, kontraktor / pekerja wajib memegang izin kerja khusus (Work Permit) yang divalidasi oleh ESR Officer & EHS Department Head.", 
        en: "Before initiating works posing extreme safety risks, contractors/operators must secure a validated Work Permit approved by the ESR Officer & EHS Department Head." 
      },
      { 
        id: "Adapun 6 Jenis Surat Izin Kerja Pekerjaan Berisiko Tinggi yaitu: [1] Izin Kerja Ketinggian, [2] Izin Kerja Perancah, [3] Izin Kerja Pengelasan (Hot Work/Welding), [4] Izin Kerja Penggalian, [5] Izin Kerja Pengangkatan Berat (Rigging/Lifting), [6] Izin Kerja Area Terbatas (Confined Space).", 
        en: "The 6 distinct High-Risk Work Permits are: [1] Working at Heights Permit, [2] Scaffolding Erection Permit, [3] Hot Work/Welding Permit, [4] Trench Excavation Permit, [5] Heavy Lifting & Rigging Permit, [6] Confined Space Entry Permit." 
      },
      { 
        id: "Pastikan tanda tangan perizinan dan checklist K3 telah terisi dan terpasang di lokasi kerja saat mulai beraktivitas.", 
        en: "Ensure the authorized signatures and safety checklists are thoroughly filled, validated, and posted at the workspace before commencing operations." 
      }
    ]
  }
];

export const SafetyInductionSlides: React.FC<SafetyInductionSlidesProps> = ({ lang }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const [viewMode, setViewMode] = useState<'pdf' | 'text'>('pdf');

  const handleNext = () => {
    setCurrentIdx((prev) => (prev === SLIDES_DATA.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setCurrentIdx((prev) => (prev === 0 ? SLIDES_DATA.length - 1 : prev - 1));
  };

  const currentSlide = SLIDES_DATA[currentIdx];

  return (
    <div className="w-full bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
      {/* Accordion Trigger Header */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-slate-900 text-white px-5 py-4 flex flex-wrap items-center justify-between cursor-pointer hover:bg-slate-800 transition select-none gap-3"
      >
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-yellow-400 shrink-0" />
          <div>
            <h3 className="font-bold text-sm tracking-wide text-left flex items-center gap-2">
              <span>{lang === 'id' ? "MATERI ACUAN SAFETY INDUCTION" : "SAFETY INDUCTION REFERENCE SLIDES"}</span>
              <span className="px-2 py-0.5 rounded-md bg-yellow-400/20 text-yellow-300 text-[10px] font-extrabold uppercase border border-yellow-400/30">
                PDF SLIDE
              </span>
            </h3>
            <p className="text-slate-400 text-xs text-left mt-0.5">
              {lang === 'id' 
                ? "Pelajari slide PDF di bawah sebagai rujukan utama untuk mengisi post-test" 
                : "Study the PDF slides below as reference to complete the post-test"
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-slate-800 px-3 py-1 rounded-xl text-xs border border-slate-700 shrink-0">
          {isOpen ? (
            <>
              <EyeOff className="w-3.5 h-3.5 text-yellow-400" />
              <span>{lang === 'id' ? 'Sembunyikan' : 'Hide'}</span>
            </>
          ) : (
            <>
              <Eye className="w-3.5 h-3.5 text-green-400" />
              <span>{lang === 'id' ? 'Tampilkan' : 'Show'}</span>
            </>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="p-3 sm:p-5 md:p-6 animate-fade-in space-y-4">
          
          {/* Mode Switcher Tabs */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-200/70 p-1.5 rounded-2xl border border-slate-300/80">
            <div className="flex items-center gap-1.5 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setViewMode('pdf')}
                className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-extrabold transition cursor-pointer select-none ${
                  viewMode === 'pdf'
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-700 hover:bg-slate-300/60'
                }`}
              >
                <FileText className="w-4 h-4 text-yellow-400" />
                <span>{lang === 'id' ? 'Dokumen PDF Slide (Google Drive)' : 'PDF Slide Document (Google Drive)'}</span>
              </button>

              <button
                type="button"
                onClick={() => setViewMode('text')}
                className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-extrabold transition cursor-pointer select-none ${
                  viewMode === 'text'
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-700 hover:bg-slate-300/60'
                }`}
              >
                <Layers className="w-4 h-4 text-amber-400" />
                <span>{lang === 'id' ? 'Ringkasan Slide (14 Topik)' : 'Slide Summary (14 Topics)'}</span>
              </button>
            </div>

            <a
              href={SAFETY_INDUCTION_PDF_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-slate-950 font-extrabold text-xs rounded-xl transition shadow-xs cursor-pointer ml-auto"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span>{lang === 'id' ? 'Buka Link Drive' : 'Open Drive Link'}</span>
            </a>
          </div>

          {/* VIEW MODE 1: PDF VIEWER (GOOGLE DRIVE) */}
          {viewMode === 'pdf' && (
            <div className="w-full rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 shadow-2xl animate-fade-in">
              <PdfViewer
                url={SAFETY_INDUCTION_PDF_URL}
                title={lang === 'id' ? "Materi Safety Induksi - PT United Tractors Tbk" : "Safety Induction Slide - PT United Tractors Tbk"}
                height="650px"
              />
            </div>
          )}

          {/* VIEW MODE 2: INTERACTIVE TEXT SLIDES */}
          {viewMode === 'text' && (
            <div className="space-y-4 animate-fade-in">
              {/* Quick jump slide indexes tabs */}
              <div className="flex flex-wrap gap-1 overflow-x-auto pb-2 border-b border-slate-200 scrollbar-thin">
                {SLIDES_DATA.map((slide, i) => (
                  <button
                    key={slide.id}
                    onClick={() => setCurrentIdx(i)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition shrink-0 ${
                      i === currentIdx 
                        ? 'bg-yellow-400 text-slate-950 font-bold shadow-sm ring-2 ring-yellow-300' 
                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    Sl. {slide.id}
                  </button>
                ))}
              </div>

              {/* Active slide card stage */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6 min-h-[220px] flex flex-col justify-between">
                <div>
                  {/* Category & Slide number block */}
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4 gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="p-1.5 bg-slate-50 rounded-lg shrink-0">
                        {currentSlide.icon}
                      </div>
                      <div className="min-w-0">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block leading-none mb-1">
                          {lang === 'id' ? "TOPIK / TOPIC" : "CATEGORY / AREA"}
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-indigo-600 uppercase truncate block">
                          {lang === 'id' ? currentSlide.category.id : currentSlide.category.en}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-[10px] sm:text-xs font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-md font-mono">
                        {currentSlide.id} / 14
                      </span>
                    </div>
                  </div>

                  {/* Slide Title */}
                  <h4 className="text-sm sm:text-base md:text-lg font-extrabold text-slate-950 uppercase tracking-tight mb-4 leading-snug">
                    {lang === 'id' ? currentSlide.title.id : currentSlide.title.en}
                  </h4>

                  {/* Bullet Points - Modern Left Accent Layout */}
                  <div className="space-y-3.5 text-left my-4">
                    {currentSlide.points.map((pt, i) => (
                      <div key={i} className="border-l-3 border-yellow-400 pl-3 py-0.5">
                        <p className="text-sm font-bold text-slate-900 leading-relaxed">{lang === 'id' ? pt.id : pt.en}</p>
                        {lang === 'id' && (
                          <p className="text-xs text-slate-500 italic font-medium mt-1 leading-normal">{pt.en}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Visual elements / tables rendering inside slides */}
                {currentSlide.visualId === 'sampah' && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-5 pt-4 border-t border-slate-100 text-[11px] font-semibold">
                    <div className="bg-green-50 border border-green-150 rounded-lg p-3 text-green-950 text-left">
                      <div className="font-bold text-green-700 flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
                        ORGANIK
                      </div>
                      <div className="text-[10px] text-green-800 font-normal mt-1 leading-relaxed">Sisa makanan, kertas, tisu kering, dedaunan.</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-150 rounded-lg p-3 text-yellow-950 text-left">
                      <div className="font-bold text-yellow-700 flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" />
                        ANORGANIK
                      </div>
                      <div className="text-[10px] text-yellow-800 font-normal mt-1 leading-relaxed">Plastik, botol minuman, kaleng logam, kaca/beling.</div>
                    </div>
                    <div className="bg-red-50 border border-red-150 rounded-lg p-3 text-red-950 text-left">
                      <div className="font-bold text-red-700 flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />
                        LIMBAH B3
                      </div>
                      <div className="text-[10px] text-red-800 font-normal mt-1 leading-relaxed">Oli bekas, aki basah/kering, kain majun kotor, sarung tangan bekas.</div>
                    </div>
                  </div>
                )}

                {currentSlide.visualId === 'golden_rules' && (
                  <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-2 mt-5 pt-4 border-t border-slate-100 text-xs font-medium text-slate-800 text-left">
                    <div className="bg-amber-50 p-2.5 rounded-lg border border-amber-150">
                      <span className="font-bold text-amber-900 block mb-0.5">1. Perjalanan:</span> Sabuk & Jaket Air
                    </div>
                    <div className="bg-amber-50 p-2.5 rounded-lg border border-amber-150">
                      <span className="font-bold text-amber-900 block mb-0.5">2. Alat Bergerak:</span> KIMPER & P2H
                    </div>
                    <div className="bg-amber-50 p-2.5 rounded-lg border border-amber-150">
                      <span className="font-bold text-amber-900 block mb-0.5">3. LOTO:</span> Padlock & Danger Tag
                    </div>
                    <div className="bg-amber-50 p-2.5 rounded-lg border border-amber-150">
                      <span className="font-bold text-amber-900 block mb-0.5">4. Ketinggian:</span> Full Body Harness
                    </div>
                    <div className="bg-amber-50 p-2.5 rounded-lg border border-amber-150">
                      <span className="font-bold text-amber-900 block mb-0.5">5. Ruang Terbatas:</span> Izin Resmi (CS)
                    </div>
                    <div className="bg-amber-50 p-2.5 rounded-lg border border-amber-150">
                      <span className="font-bold text-amber-900 block mb-0.5">6. Alat Angkat:</span> SWL Limit
                    </div>
                  </div>
                )}

                {currentSlide.visualId === 'rambu' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mt-5 pt-4 border-t border-slate-100 text-[11px] font-semibold text-left">
                    <div className="border-l-4 border-blue-500 bg-blue-50/60 p-2.5 rounded-r-lg">
                      <span className="text-blue-800 font-bold block mb-0.5">1. BIRU (Mandatory)</span> Wajib Dikuti. Contoh: Gunakan APD Lengkap.
                    </div>
                    <div className="border-l-4 border-yellow-500 bg-yellow-50/60 p-2.5 rounded-r-lg">
                      <span className="text-yellow-800 font-bold block mb-0.5">2. KUNING (Warning)</span> Awas Bahaya / Risiko. Contoh: Tegangan Tinggi.
                    </div>
                    <div className="border-l-4 border-red-500 bg-red-50/60 p-2.5 rounded-r-lg">
                      <span className="text-red-800 font-bold block mb-0.5">3. MERAH (Prohibition)</span> Dilarang. Contoh: No Smoking.
                    </div>
                    <div className="border-l-4 border-green-500 bg-green-50/60 p-2.5 rounded-r-lg">
                      <span className="text-green-800 font-bold block mb-0.5">4. HIJAU (Emergency)</span> Jalur Evakuasi, Kotak P3K, Assembly Point.
                    </div>
                  </div>
                )}

                {currentSlide.visualId === 'permit' && (
                  <div className="bg-indigo-50 border border-indigo-150 rounded-lg p-3 mt-4 text-left text-xs text-indigo-950 font-medium leading-relaxed">
                    <span className="font-bold text-indigo-900 block mb-1">6 High-Risk Work Permits wajib diisi:</span>
                    Ketinggian, Perancah (Scaffolding), Pengelasan (Hot Work), Penggalian (Excavation), Pengangkatan Berat (Rigging/Lifting), Area Sempit (Confined Space).
                  </div>
                )}
              </div>

              {/* Slider Controllers */}
              <div className="flex items-center justify-between mt-5 pt-3 border-t border-slate-200 gap-2">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="flex items-center gap-1.5 bg-white hover:bg-slate-50 active:bg-slate-100 border border-slate-300 text-slate-700 px-3.5 py-2 rounded-xl text-sm font-semibold transition cursor-pointer select-none"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden xs:inline">{lang === 'id' ? "Sebelumnya" : "Previous"}</span>
                  <span className="inline xs:hidden">{lang === 'id' ? "Sblm" : "Back"}</span>
                </button>

                <div className="text-[11px] sm:text-xs text-slate-500 font-mono font-bold">
                  {currentSlide.id} / 14
                </div>

                <button
                  type="button"
                  onClick={handleNext}
                  className="flex items-center gap-1.5 bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 text-slate-950 px-3.5 py-2 rounded-xl text-sm font-extrabold transition cursor-pointer shadow-sm select-none"
                >
                  <span className="hidden xs:inline">{lang === 'id' ? "Selanjutnya" : "Next"}</span>
                  <span className="inline xs:hidden">{lang === 'id' ? "Lanjut" : "Next"}</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
